import { computed, ref } from 'vue';
import { useOrderStore } from '@/store/orders';
import { storeToRefs } from 'pinia';
import { OfferTypes, useOfferStore } from '@/store/offers';
import { useI18n } from 'vue-i18n';
import { handleDownload, handleDownloadBlob } from '@/helpers/util';
import { useSnackbarStore } from '@/store/snackbar';
import { useApi } from '@/composables/useApi';
import { useInvoiceStore } from '@/store/invoices';
import { useAppStore } from '@/store/store';
import { useNetoperatorStore } from '@/store/netoperator';
import { useInstallationStore } from '@/store/installations';

/**
 * @typedef Document
 * @type {object}
 * @property {string} name - the document internal name.
 * @property {string} text - the document text / label.
 * @property {string} downloadName - the document's name on download.
 * @property {string} url - the document's download url.
 * @property {boolean} downloading - is the document in the process of downloading.
 * @property {boolean} cdn - is the document available from cdn.
 */

export function useInstallationDocuments() {
  const { t } = useI18n();
  const { showError } = useSnackbarStore();
  const { apiGet } = useApi();

  const { fluviusDoc } = storeToRefs(useAppStore());

  const orderStore = useOrderStore();
  const { getOrderByID } = orderStore;
  const { order } = storeToRefs(orderStore);

  const offerStore = useOfferStore();
  const { getOfferAttachments, getOfferDocument } = offerStore;

  const invoiceStore = useInvoiceStore();
  const { getInvoiceByID, invoiceTitle } = invoiceStore;

  const installationStore = useInstallationStore();
  const {
    getInstallationInspection,
  } = installationStore;

  const netoperatorStore = useNetoperatorStore();
  const { getNetOperatorByID } = netoperatorStore;
  const { netOperatorFluvius } = storeToRefs(netoperatorStore);

  const documents = ref([]);

  const fluviusDocument = computed(() => fluviusDoc.value ? getCdnDocument(fluviusDoc.value, t('netoperator_infrax.or.eandis_manual'), `${t('netoperator_installationHelp')}.pdf`) : null);

  async function getInstallationDocuments(installation) {
    documents.value = [];
    if (installation.offerId > 0) {
      documents.value.push(getOfferDocument(installation));

      const attachments = await getOfferAttachments(installation.offerId, installation.versionNumber);
      if (attachments) {
        documents.value = [...documents.value, ...attachments];
      }
    }
    if (installation.orderId > 0 && !installation.orderStatusInitial) {
      if (installation.orderId !== order.value?.id) {
        await getOrderByID(installation.orderId);
      }

      documents.value = [...documents.value, ...getOrderDocs(order.value)];
    }
    if (installation.orderDeliveriesId > 0) {
      documents.value = [...documents.value, ...getDeliveryDocs(installation)];
    }

    documents.value = [...documents.value, ...await getInstallationInvoiceDocs(installation)];

    documents.value = [...documents.value, ...await getInstallationNetOperatorDocs(installation)];

    return documents.value;
  }

  function getOrderDocs(order) {
    let docs = [];

    docs.push({
      name: 'order',
      downloading: false,
      text: t('orderDetail.order'),
      downloadName: `${t('orderDetail.order')}-${order.id}.pdf`,
      url: `orders/${order.id}/files/${order.offerTypeId}?type=print`,
    });

    if (order.attachments) {
      docs = [...docs, ...order.attachments];
    }

    // keuringattest
    if (order?.keuringCertificates.length > 0) {
      order.keuringCertificates.forEach((keuringCertificate, index) => {
        const textAppend = order.keuringCertificates.length > 1 ? ' ' + (index + 1) : '';
        docs.push({
          cdn: true,
          url: keuringCertificate.url,
          text: t('netoperator_inspectionReport') + textAppend,
          name: 'keuringCertificate',
        });
      });
    }
    // wiring voor keuring
    if (order?.hasWiringDiagram && order?.wiringDiagramKeurings.length === 0) {
      docs.push({
        cdn: false,
        text: t('netoperator_electrical_schema'),
        name: 'wiringDiagramDoc',
        downloadName: `${t('netoperator_electrical_schema')}-${order.id}.pdf`,
        url: `files/sales/orders/${order.id}/wiringDiagramDoc`,
      });
    }
    // wiring na keuring
    if (order?.wiringDiagramKeurings.length > 0) {
      order.wiringDiagramKeurings.forEach((wiringDiagramKeuring, index) => {
        const textAppend = order.wiringDiagramKeurings.length > 1 ? ' ' + (index + 1) : '';
        docs.push({
          cdn: true,
          url: wiringDiagramKeuring.url,
          text: t('netoperator_electrical_inspection') + textAppend,
          name: 'wiringDiagramKeuring',
        });
      });
    }

    // ichoosr
    if (order.orderDeliveriesId && order.hasExternalPrepayment) {
      docs.push({
        name: 'orderIchoosrOpleverdoc',
        downloading: false,
        text: t('iChoosr.document_name'),
        downloadName: `${t('iChoosr.document_name')}-${order.id}.pdf`,
        url: `deliveries/${order.orderDeliveriesId}/iChooserFiles`,
      });
    }

    return docs;
  }

  function getDeliveryDocs(installation) {
    const docs = [];

    docs.push({
      name: 'delivery',
      downloading: false,
      text: t('deliveryDetail.delivery'),
      downloadName: `${t('deliveryDetail.delivery')}-${installation.orderDeliveriesId}.pdf`,
      url: `deliveries/${installation.orderDeliveriesId}/files/${installation.offerTypeId}?type=print`,
    });

    return docs;
  }

  async function getInstallationInvoiceDocs(installation) {
    let docs = [];

    for (let tempInvoice of installation.prepaymentInvoices) {
      tempInvoice = await getInvoiceByID(tempInvoice.id);

      docs = [...docs, ...getInvoiceDocs(tempInvoice)];
    }

    for (let tempInvoice of installation.finalInvoices) {
      tempInvoice = await getInvoiceByID(tempInvoice.id);

      docs = [...docs, ...getFinalInvoiceDocs(tempInvoice)];
    }

    return docs;
  }

  async function getInstallationNetOperatorDocs(installation) {
    const docs = [];

    const inspection = await getInstallationInspection(installation.id);

    if (!inspection.inspectionId) {
      return docs;
    }

    await getNetOperatorByID(inspection.inspectionId, inspection.inspectionSource);

    if (
      netOperatorFluvius.value
      && fluviusDocument.value
      && ([OfferTypes.SolarPanels, OfferTypes.Batteries].includes(installation.offerTypeId))
    ) {
      docs.push(fluviusDocument.value);
    }

    return docs;
  }

  function getInvoiceDocs(invoice) {
    let documents = [];
    const text = invoiceTitle(invoice);

    documents.push({
      name: 'invoice',
      downloading: false,
      text,
      downloadName: `${text}-${invoice.invoiceNrPrefix}${invoice.invoiceNr}.pdf`,
      url: `invoices/${invoice.id}/files/${invoice.offerTypeId}?type=print`,
    });

    if (invoice.attachments) {
      documents = [...documents, ...invoice.attachments];
    }

    return documents;
  }

  function getFinalInvoiceDocs(invoice) {
    let documents = [];
    let title = invoiceTitle(invoice);

    documents = [...documents, ...getInvoiceDocs(invoice)];

    if (['RESA', 'ORES', 'AIESH', 'REW', 'AIESH'].includes(invoice?.netOperator)) {
      // doc mise en service
      documents.push({
        cdn: false,
        text: t('netoperator_form'),
        name: 'declaration-RGIE',
        downloadName: `${t('netoperator_form')}-${invoice.id}.pdf`,
        url: `files/sales/invoices/${invoice.id}/declaration-RGIE`,
      });
      // fotos not fluvius
      documents.push({
        cdn: false,
        text: t('netoperator_pictures'),
        name: 'picturesReference',
        downloadName: `${t('netoperator_pictures')}-${invoice.id}.pdf`,
        url: `files/sales/invoices/${invoice.id}/picturesReference`,
      });
    } else if (['INFRAX', 'EANDIS', 'Fluvius'].includes(invoice.netOperator)) {
      documents.push({
        cdn: false,
        text: `${t('netoperator_installationHelp')} - ${title}`,
        name: 'PVHelpByRegistration',
        downloadName: `${t('netoperator_installationHelp')} - ${title}.pdf`,
        url: `files/sales/invoices/${invoice.id}/PVHelpByRegistration`,
      });
    }

    return documents;
  }

  async function getInstallationDocumentsByName(installation, docNames) {
    await getInstallationDocuments(installation);

    return documents.value.reduce((acc, doc) => {
      if (docNames.includes(doc.name)) {
        acc.push(doc);
      }

      return acc;
    }, []);
  }

  function getOrderDocByName(order, docName) {
    const documents = getOrderDocs(order);

    return documents.find((doc) => doc.name === docName);
  }

  /**
   * handle downloading of document
   * @param {Document} _document
   * @returns {Promise<void>}
   */
  async function download(_document) {
    _document.downloading = true;
    try {
      if (_document.cdn) {
        downloadFromCDN(_document);
      } else {
        await downloadFromApi(_document);
      }
    } catch (error) {
      console.log(error);
      showError(t('fileError'));
    }
    _document.downloading = false;
  }

  /**
   * Open Document in tab from cdn url
   * @param {Document} _document
   * @returns {void}
   */
  function downloadFromCDN(_document) {
    const url = URL.parse(_document.url);

    url.searchParams.append('no_redirect', true);
    url.searchParams.append('download', true);

    handleDownload(_document.downloadName, url.toString());
  }

  /**
   * Open Document in tab from cdn url
   * @param {Document} _document
   * @returns {Promise<void>}
   */
  async function downloadFromApi(_document) {
    const response = await apiGet(_document.url, 'blob');

    if (response.status === 200) {
      handleDownloadBlob(_document.downloadName, response.data);
    } else {
      showError(t('fileError'));
    }
  }

  function getArticleDocument(article) {
    const articleName = article.name || article.code;

    return getCdnDocument(article.url, articleName, 'manual');
  }

  function getCdnDocument(url, text, name, downloadName = '') {
    if (!downloadName) {
      downloadName = text;
    }

    return {
      name,
      cdn: true,
      downloading: false,
      downloadName,
      text,
      url,
    };
  }

  return {
    fluviusDocument,
    getInstallationDocuments,
    getOrderDocByName,
    download,
    getInstallationDocumentsByName,
    getArticleDocument,
  };
}
